'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// TabBar — the canonical tab strip + optional action row used across
// detail pages, filter shelves, and any other tabbed surface.
//
// Composes Radix Tabs primitives (Tabs.Root / Tabs.List / Tabs.Trigger)
// for keyboard / ARIA / roving-tabindex wiring, and layers a
// measurement-based responsive strategy on top:
//
//   • At wide widths: tab strip + actions row on a single line, with
//     trailing tabs collapsing into a "More" dropdown when the strip is
//     wider than the available space.
//   • At medium widths: the layout stacks — by default the actions row
//     takes its own line ABOVE the tabs (real DOM order, so focus and
//     reading order match the visuals; `stackedActionsPlacement="below"`
//     opts a consumer out when its actions are subordinate to the tabs)
//     and the tab strip gets the full container width, still using
//     "More" overflow as needed. Stacking is detected from rendered
//     geometry after each commit, never predicted from widths.
//   • At narrow widths (tab strip can't fit even one pill + More):
//     the tabs collapse into a single select-style trigger
//     ("Active Tab ▾") that opens a dropdown of every tab, keeping the
//     same stacked ordering rules.
//
// The active tab is always reachable: it's either in the visible strip
// (with width-aware promotion that re-fits the strip afterwards) or it
// is the label of the select-style trigger.
//
// `overflowActions` are items that ALWAYS live in the action menu and
// never render inline ("Leader tips", "Mute notifications").
//
// The hidden measurement layer renders every tab's pill at its natural
// pixel width so fit calculations don't depend on which tabs are
// currently visible — no measure-then-mutate feedback loop.
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { Button } from '../Button/Button';
import { DropdownMenu, DropdownMenuItem, } from '../client/DropdownMenu/DropdownMenu';
import { Row } from '../server/layout/Row/index';
import { I, Icon } from '../system/icons';
import { CONTROL_HEIGHT } from '../system/sizing';
import { TextTrim } from '../system/text-trim';
import styles from './TabBar.module.css';
const TAB_GAP_PX = 4; // matches Row gap="xs" → var(--space-xs)
const OUTER_GAP_PX = 12; // matches outer Row gap="md" → var(--space-md)
const MORE_TRIGGER_FALLBACK_PX = 88;
// Do the tabs side and the actions side share a flex line? Same line ⇔
// vertical ranges overlap (align-center makes bare offsetTop comparison
// unreliable across different item heights). The check is order-independent:
// with exactly two flex items, whether they share a line depends only on
// their widths, so swapping their DOM order cannot flip the result — no
// re-render oscillation.
function isStacked(tabsSide, actionsSide) {
    if (!tabsSide || !actionsSide)
        return false;
    const a = tabsSide.getBoundingClientRect();
    const b = actionsSide.getBoundingClientRect();
    return !(a.top < b.bottom && b.top < a.bottom);
}
function formatCount(n) {
    if (n < 1000)
        return String(n);
    return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, '')}k`;
}
// Pill style for both the visible Tabs.Trigger and the hidden
// measurement layer's mock triggers. Inline so the same shape is used in
// both render paths; hover state lives in TabBar.module.css and reads
// the `--tab-pill-hover-*` custom properties set here.
function tabPillStyle(active) {
    return {
        background: active ? 'var(--foreground)' : 'transparent',
        color: active ? 'var(--background)' : 'var(--muted-foreground)',
        border: 'none',
        borderRadius: 'var(--radius-full)',
        height: CONTROL_HEIGHT.md,
        padding: '0 var(--space-md)',
        font: 'var(--type-label)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-sm)',
        '--tab-pill-hover-bg': 'var(--background)',
        '--tab-pill-hover-ink': 'var(--foreground)',
    };
}
// Labels go through TextTrim like every other control primitive (Button,
// Pill, Badge). Without it the pill centers its full line box while the
// adjacent "More" Button centers a cap-trimmed one, and the two labels sit
// ~1px off from each other in the same row.
function TabPillContent({ item, active, }) {
    return (_jsxs(_Fragment, { children: [_jsx(TextTrim, { children: item.label }), typeof item.count === 'number' && item.count > 0 ? (_jsx("span", { "aria-hidden": "true", style: { display: 'contents' }, children: _jsx(TextTrim, { style: {
                        font: 'var(--type-label-sm)',
                        color: active ? 'var(--background)' : 'var(--muted-foreground)',
                        opacity: active ? 0.6 : 0.75,
                        fontVariantNumeric: 'tabular-nums',
                    }, children: formatCount(item.count) }) })) : null] }));
}
// Width consumed by N tab pills laid out in a row with TAB_GAP_PX between
// them. Optionally adds a "More" trigger at the end (its own preceding gap
// included). Single source of truth for fit math, used by both the
// everything-fits short-circuit and the greedy fit loop below.
function rowWidth(ids, widths, withMore) {
    if (ids.length === 0)
        return withMore;
    const sum = ids.reduce((acc, id, i) => acc + (widths.get(id) ?? 0) + (i === 0 ? 0 : TAB_GAP_PX), 0);
    return sum + (withMore > 0 ? TAB_GAP_PX + withMore : 0);
}
// Pick which tabs to render inline, given the available width budget and
// the active tab that must remain visible.
//
//   • Everything fits → return all tabs, no "More".
//   • Pinned pill + "More" trigger fits → greedy-fill the rest in id order,
//     reserving the pinned pill at its natural width and re-inserting it
//     at its original position in the result.
//   • Even the pinned pill + "More" can't fit → select mode (single tab
//     returned for use as the dropdown trigger label).
//
// Returns at least one id when input is non-empty.
function fitTabs(ids, widths, available, moreWidth, pinned) {
    if (ids.length === 0)
        return { tabs: [], selectMode: false };
    if (rowWidth(ids, widths, 0) <= available) {
        return { tabs: [...ids], selectMode: false };
    }
    const pinnedId = pinned !== undefined && ids.includes(pinned) ? pinned : ids[0];
    if (rowWidth([pinnedId], widths, moreWidth) > available) {
        return { tabs: [pinnedId], selectMode: true };
    }
    const fitting = [pinnedId];
    for (const id of ids) {
        if (id === pinnedId)
            continue;
        const candidate = [...fitting, id];
        if (rowWidth(candidate, widths, moreWidth) > available)
            break;
        fitting.push(id);
    }
    const orderMap = new Map(ids.map((id, idx) => [id, idx]));
    fitting.sort((a, b) => (orderMap.get(a) ?? 0) - (orderMap.get(b) ?? 0));
    return { tabs: fitting, selectMode: false };
}
export function TabBar({ items, value, onChange, actions: actionsProp, overflowActions: overflowActionsProp, actionsMenuLabel = 'More actions', align = 'start', overflow = 'menu', stackedActionsPlacement = 'above', ariaLabel, }) {
    const isScroll = overflow === 'scroll';
    const actions = useMemo(() => actionsProp ?? [], [actionsProp]);
    const overflowActions = useMemo(() => overflowActionsProp ?? [], [overflowActionsProp]);
    const containerRef = useRef(null);
    const tabsMeasureRef = useRef(null);
    const tabsSideRef = useRef(null);
    const actionsRowRef = useRef(null);
    const moreTabsTriggerMeasureRef = useRef(null);
    const itemIds = useMemo(() => items.map((i) => i.id), [items]);
    const [visibleTabIds, setVisibleTabIds] = useState(() => itemIds);
    const [selectMode, setSelectMode] = useState(false);
    // Whether the outer Row has ACTUALLY wrapped into a stacked layout —
    // measured from rendered geometry after every commit (do the tabs side and
    // the actions side share a flex line?), never predicted from widths. When
    // stacked and `stackedActionsPlacement` is 'above', the actions render
    // before the tabs in the DOM — a real reorder, so keyboard and
    // screen-reader order match the visual order.
    const [wrapped, setWrapped] = useState(false);
    // Re-check after every commit (DOM/content changes) AND on every container
    // resize. The resize path needs its own observer: a window resize
    // re-lays-out WITHOUT a commit whenever the fit result happens not to
    // change (and scroll mode never runs the fit pass at all), so an
    // after-commit check alone goes stale. Wrap/unwrap itself changes the
    // container height, so the observer re-fires once more and converges —
    // isStacked is order-independent, so it cannot oscillate.
    useLayoutEffect(() => {
        setWrapped(isStacked(tabsSideRef.current, actionsRowRef.current));
    });
    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        let raf = 0;
        const measure = () => setWrapped(isStacked(tabsSideRef.current, actionsRowRef.current));
        const ro = new ResizeObserver(() => {
            measure();
            // The observer can deliver before the final flex re-layout of this
            // resize (notably under devtools viewport emulation). When the
            // possibly-stale measurement matches current state, no commit follows
            // and nothing else would re-check — so always re-measure on the next
            // frame, when layout has settled.
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(measure);
        });
        ro.observe(container);
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, []);
    useLayoutEffect(() => {
        // Scroll mode renders every tab inline and lets the strip scroll — no
        // fit measurement, no "More"/select collapse.
        if (isScroll)
            return;
        const container = containerRef.current;
        if (!container)
            return;
        const remeasure = () => {
            const containerWidth = container.getBoundingClientRect().width;
            if (containerWidth === 0)
                return;
            // Read tab widths from the hidden measurement layer.
            const tabWidths = new Map();
            const tabsLayer = tabsMeasureRef.current;
            if (tabsLayer) {
                tabsLayer
                    .querySelectorAll('[data-measure-tab]')
                    .forEach((n) => {
                    const id = n.dataset['measureTab'];
                    if (id)
                        tabWidths.set(id, n.getBoundingClientRect().width);
                });
            }
            const moreTabsWidth = moreTabsTriggerMeasureRef.current?.getBoundingClientRect().width ??
                MORE_TRIGGER_FALLBACK_PX;
            // The action row renders inline at its natural width (flexShrink:0).
            // Measure it directly off the rendered row; tabs get whatever's left
            // when both share a single line.
            const hasAnyActions = actions.length > 0 || overflowActions.length > 0;
            const actionsRowWidth = hasAnyActions
                ? actionsRowRef.current?.getBoundingClientRect().width ?? 0
                : 0;
            const outerGap = hasAnyActions ? OUTER_GAP_PX : 0;
            // Wrap detection: when the natural-width single-line layout would
            // overflow the container, the outer Row's `wrap` kicks in and the
            // actions row drops below the tabs. In that wrapped layout the
            // tabs get the full container width.
            const tabsNaturalWidth = rowWidth(itemIds, tabWidths, 0);
            const singleLineWidth = tabsNaturalWidth + outerGap + actionsRowWidth;
            const willWrap = singleLineWidth > containerWidth;
            const tabsAvailable = willWrap
                ? containerWidth
                : Math.max(0, containerWidth - actionsRowWidth - outerGap);
            const fit = fitTabs(itemIds, tabWidths, tabsAvailable, moreTabsWidth, value);
            setVisibleTabIds((prev) => prev.length === fit.tabs.length &&
                prev.every((id, i) => id === fit.tabs[i])
                ? prev
                : [...fit.tabs]);
            setSelectMode((prev) => (prev === fit.selectMode ? prev : fit.selectMode));
        };
        remeasure();
        const ro = new ResizeObserver(remeasure);
        ro.observe(container);
        return () => ro.disconnect();
    }, [itemIds, actions, overflowActions, value, isScroll]);
    // Scroll mode: keep the active tab in view when it changes (e.g. selected
    // from off-screen), so the current scope is never hidden past the edge.
    //
    // Nudges the strip's own scrollLeft rather than calling
    // `el.scrollIntoView()`. scrollIntoView walks EVERY scrollable ancestor,
    // including the page's vertical scroller (AppShell's <main>) — so on mount
    // it dragged the whole page up to reveal the tab strip, wiping the router's
    // restored scroll offset when a reader came back to a scrolled feed.
    // Horizontal placement of a tab is this strip's business and nothing else's.
    useLayoutEffect(() => {
        if (!isScroll || value === undefined)
            return;
        const strip = containerRef.current?.querySelector('[role="tablist"]');
        const el = strip?.querySelector(`[data-tab-id="${CSS.escape(value)}"]`);
        if (!strip || !el)
            return;
        const stripBox = strip.getBoundingClientRect();
        const tabBox = el.getBoundingClientRect();
        const overshootLeft = tabBox.left - stripBox.left;
        const overshootRight = tabBox.right - stripBox.right;
        if (overshootLeft < 0) {
            strip.scrollBy({ left: overshootLeft, behavior: 'smooth' });
        }
        else if (overshootRight > 0) {
            strip.scrollBy({ left: overshootRight, behavior: 'smooth' });
        }
    }, [isScroll, value]);
    // Scroll mode: translate wheel / two-finger trackpad gestures into
    // horizontal movement. A trackpad swipe (and a mouse wheel) is usually
    // vertical-dominant, which the browser routes to the nearest *vertical*
    // scroller (the page) — so without this the strip won't move. We take the
    // larger-magnitude axis, scroll the strip, and preventDefault; at either
    // edge we release so the page can scroll normally. Attached natively
    // (not React onWheel) because preventDefault needs a non-passive listener.
    useEffect(() => {
        if (!isScroll)
            return;
        // The scroll strip is the outer role=tablist Row (it wraps the inner
        // Tabs.List, so it's first in DOM order) that carries overflow-x.
        const el = containerRef.current?.querySelector('[role="tablist"]');
        if (!el)
            return;
        const onWheel = (e) => {
            if (el.scrollWidth <= el.clientWidth)
                return;
            const delta = Math.abs(e.deltaX) >= Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
            if (delta === 0)
                return;
            const atStart = el.scrollLeft <= 0;
            const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
            if ((delta < 0 && atStart) || (delta > 0 && atEnd))
                return;
            el.scrollLeft += delta;
            e.preventDefault();
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [isScroll, itemIds]);
    const itemMap = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
    const visibleTabSet = useMemo(() => new Set(visibleTabIds), [visibleTabIds]);
    const visibleTabs = visibleTabIds
        .map((id) => itemMap.get(id))
        .filter((x) => x !== undefined);
    const overflowTabs = items.filter((i) => !visibleTabSet.has(i.id));
    // Scroll mode never runs the fit pass, so render the full list directly
    // (visibleTabIds can go stale when items change while measurement is off).
    const inlineTabs = isScroll ? items : visibleTabs;
    const hasAnyActions = actions.length > 0 || overflowActions.length > 0;
    const showActionMenu = overflowActions.length > 0;
    // In select mode, the strip collapses into a single dropdown trigger
    // labeled with the active tab. Fall back to the first item when no
    // value is supplied so the trigger always has something to render.
    const selectActive = (value !== undefined ? itemMap.get(value) : undefined) ?? items[0];
    return (_jsxs("div", { ref: containerRef, className: styles.container, children: [_jsx("div", { className: styles.measureLayer, "aria-hidden": true, children: _jsxs("div", { ref: tabsMeasureRef, className: styles.measureGroup, children: [items.map((item) => (_jsx("button", { type: "button", "data-measure-tab": item.id, style: tabPillStyle(false), tabIndex: -1, children: _jsx(TabPillContent, { item: item, active: false }) }, item.id))), _jsx("div", { ref: moreTabsTriggerMeasureRef, children: _jsx(Button, { variant: "ghost", size: "md", iconRight: I.arrowDown, children: "More" }) })] }) }), (() => {
                const tabsSide = (_jsx("div", { ref: tabsSideRef, style: { display: 'flex', minWidth: 0 }, children: !isScroll && selectMode && selectActive ? (_jsx(DropdownMenu, { align: "start", trigger: _jsxs("button", { type: "button", className: styles.tabPill, style: tabPillStyle(true), "aria-haspopup": "menu", children: [_jsx(TabPillContent, { item: selectActive, active: true }), _jsx(Icon, { size: "sm", children: I.arrowDown })] }), children: items.map((item) => (_jsxs(DropdownMenuItem, { onSelect: () => onChange?.(item.id), children: [item.label, typeof item.count === 'number' && item.count > 0
                                    ? ` (${formatCount(item.count)})`
                                    : ''] }, item.id))) })) : (_jsx(Tabs.Root, { value: value, ...(onChange !== undefined ? { onValueChange: onChange } : {}), asChild: true, children: _jsxs(Row, { role: "tablist", ...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {}), gap: "xs", align: "center", className: isScroll ? styles.scrollStrip : undefined, style: { minWidth: 0 }, children: [_jsx(Tabs.List, { asChild: true, children: _jsx(Row, { gap: "xs", align: "center", style: isScroll ? { flexWrap: 'nowrap' } : { minWidth: 0 }, children: inlineTabs.map((item) => (_jsx(Tabs.Trigger, { value: item.id, "data-tab-id": item.id, className: styles.tabPill, style: tabPillStyle(item.id === value), children: _jsx(TabPillContent, { item: item, active: item.id === value }) }, item.id))) }) }), !isScroll && overflowTabs.length > 0 ? (_jsx(DropdownMenu, { align: "end", trigger: _jsx(Button, { variant: "ghost", size: "md", iconRight: I.arrowDown, children: "More" }), children: overflowTabs.map((item) => (_jsx(DropdownMenuItem, { onSelect: () => onChange?.(item.id), children: item.label }, item.id))) })) : null] }) })) }));
                const actionsSide = hasAnyActions ? (
                // Wrapper inherits flex defaults (shrink:1) so that on a wrapped
                // outer line where the actions get the full container width, the
                // inner Row can multi-line if natural width still exceeds the
                // container — preventing the trailing kebab from clipping at
                // narrow widths.
                _jsx("div", { ref: actionsRowRef, style: { display: 'flex', minWidth: 0 }, children: _jsxs(Row, { gap: "sm", rowGap: "sm", align: "center", wrap: true, children: [actions.map((a) => (_jsx(Fragment, { children: a.inline }, a.id))), showActionMenu ? (_jsx(DropdownMenu, { align: "end", trigger: _jsx(Button, { variant: "ghost", size: "md", icon: I.more, "aria-label": actionsMenuLabel }), children: overflowActions.map((o, i) => (_jsx(DropdownMenuItem, { onSelect: o.onSelect, destructive: o.destructive ?? false, disabled: o.disabled ?? false, children: o.label }, `__overflow-${i}`))) })) : null] }) })) : null;
                const actionsFirst = wrapped && hasAnyActions && stackedActionsPlacement === 'above';
                return (_jsx(Row, { align: "center", justify: align === 'center' ? 'center' : 'space-between', gap: "md", rowGap: "sm", wrap: true, 
                    // Fill the container so justify has free space to distribute —
                    // otherwise the flex row shrink-wraps its content and both
                    // 'center' and 'space-between' collapse to the left edge.
                    style: { width: '100%' }, children: actionsFirst ? (_jsxs(_Fragment, { children: [actionsSide, tabsSide] })) : (_jsxs(_Fragment, { children: [tabsSide, actionsSide] })) }));
            })()] }));
}
//# sourceMappingURL=TabBar.js.map